import { SectionContainer } from "../molecules/section-container";
import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { RectangleList } from "../molecules/reactangle-container-list";
import { Button } from "../atoms/button";
import { usePayouts } from "../../providers/payout-provider";
import { ErrorAlert } from "../atoms/error-alert";
import { getUserFriendlyPayoutError, renderErrorMessageWithLink } from "../../utils/payout-error-messages";

const apiUrl = process.env.REACT_APP_API_URL;

const formatLocalDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const HistoryPage = () => {
  const auth = useAuth() as { id: string | null; user: any } | null;
  const navigate = useNavigate();
  const [history, setHistory] = useState<
    { id: string; date: string; type: string; status: string }[]
  >([]);
  const { fetchPayoutData, payoutData } = usePayouts();

  useEffect(() => {
    if (auth?.user) {
      fetchPayoutData(auth?.user?.accessToken)
        .then()
        .catch(() => navigate("/"));
    }
  }, [auth?.user]);

  useEffect(() => {
    const userUrl = `${apiUrl}/users`;
    const apiResponseAsync = fetch(
      `${userUrl}/${auth?.user?.email as string}/`,
      {
        headers: {
          Authorization: `bearer ${auth?.user?.accessToken}`,
        },
      },
    );
    apiResponseAsync.then((apiResponse) => {
      if (!apiResponse.ok) {
        let errorDetail =
          "Failed to retrieve user information from the database.";
        apiResponse
          .json()
          .then((errorData) => {
            errorDetail = errorData.detail || errorDetail;
            throw new Error(errorDetail);
          })
          .catch((jsonError) => {
            console.error("Error parsing backend error response:", jsonError);
            throw new Error(errorDetail);
          });
      }
      apiResponse
        .json()
        .then((dbUser) => {
          const historyUrl = `${apiUrl}/submission/`;
          return fetch(`${historyUrl}?user_id=${dbUser?.id}`, {
            headers: {
              Authorization: `bearer ${auth?.user?.accessToken}`,
            },
          });
        })
        .then((response) => response.json())
        .then((payload) => {
          setHistory(() =>
            payload.submissions.map((submission: any) => ({
              id: submission.id,
              date: submission.created_at,
              type: submission.category,
              status: submission.status,
            })),
          );
        });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SectionContainer title="Perle Collect - History">
      <ErrorAlert message={renderErrorMessageWithLink(getUserFriendlyPayoutError(payoutData?.payout_error_message))} />
      <h2 className="text-left mb-2">Submission History</h2>
      <RectangleList
        items={history.map((historyItem) => (
          <div className="flex justify-between items-center">
            <div className="text-left">
              <h3 className="text-left">{historyItem.type}</h3>
              <div className="text-sm text-dark-gray-1 font-bold">
                Submitted: {formatLocalDate(historyItem.date)}
              </div>
              <div className="text-sm text-dark-gray-1 font-bold">
                Reward: $.25
              </div>
            </div>
            <span className="p-1 bg-gray-200 rounded">
              {historyItem.status.toUpperCase()}
            </span>
          </div>
        ))}
      />
      <br />
      {history.length > 0 && (
        <>
          <h3 className="text-left">Your Balance</h3>
          <p className="mb-4 text-left">
            Payments are sent automatically after all your tasks are reviewed by
            QA an included in the next payout run. Please note that rejected
            tasks will not be paid.
          </p>
          <div className="rounded-xl border-dark-gray border-2 flex justify-around p-4 mb-4">
            <div className="flex flex-col">
              <h4>Approved Balance</h4>
              <span className="text-[2em] font-bold text-progress-ok">
                $ {payoutData.total_approved}
              </span>
            </div>
            <div className="w-[2px] h-[4.2em] border-dark-gray border" />
            <div className="flex flex-col">
              <h4>Earnings in Review</h4>
              <span className="text-[2em] font-bold text-gray-500">
                $ {payoutData.total_in_review}
              </span>
            </div>
          </div>
          <a
            href=""
            className="underline text-sm"
            onClick={(e) => {
              e.preventDefault();
              navigate("/payment-history");
            }}
          >
            View Payment History
          </a>
        </>
      )}
      <div className="mt-6 mb-4">
        <Button
          type="primary"
          text="Go to task selection"
          onClick={() => navigate("/")}
        />
      </div>
    </SectionContainer>
  );
};
